/**
 * No-black-box Expectation Match breakdown (docs/specs/discipline-rating-fit-score.md
 * Story E3). Renders every Primary's contribution to the Expectation dimension read
 * from the immutable Fit Score snapshot's `inputs.expectation` — never recomputed
 * live, so this always matches the score actually persisted.
 *
 * memberScore = max(0, 100 − P × |Discipline Rating − Expectation Level|)
 * ExpectationMatch = Σ(memberScore × points) ÷ Σ(points)   — Primaries only
 *
 * Over-qualification (rating > level) and under-qualification (rating < level) are
 * both penalties — symmetric decay (system-design non-negotiable #8) — but are
 * labeled distinctly so the direction is never ambiguous (Story E3 AC2).
 */

const ROLE_COLOR: Record<string, string> = {
  R1: "var(--role-r1)",
  R2: "var(--role-r2)",
  R3: "var(--role-r3)",
  R4: "var(--role-r4)",
};

export interface ExpectationBreakdownMemberView {
  resourceId: string;
  resourceName: string;
  role: string;
  assignmentDisciplineCode: string | null;
  rating: number;
  deviation: number;
  memberScore: number;
  points: number;
}

export interface ExpectationBreakdownData {
  expectationLevel: number;
  penaltyStep: number;
  /** The persisted Expectation Match dimension score (0–100). */
  overall: number;
  members: ExpectationBreakdownMemberView[];
}

function directionBadge(rating: number, level: number, penaltyStep: number) {
  const deviation = Math.abs(rating - level);
  const loss = penaltyStep * deviation;
  if (deviation === 0) {
    return {
      label: "Match",
      loss: 0,
      className: "bg-success-100 text-success-700",
    };
  }
  if (rating > level) {
    return {
      label: `Over-qualified · −${loss}`,
      loss,
      className: "bg-info-100 text-info-700",
    };
  }
  return {
    label: `Under-qualified · −${loss}`,
    loss,
    className: "bg-[var(--fit-at-risk-bg)] text-[var(--fit-at-risk)]",
  };
}

export function ExpectationBreakdown({ data }: { data: ExpectationBreakdownData | null }) {
  if (!data) {
    return (
      <div className="rounded-md border border-dashed border-border bg-card/50 px-3 py-4 text-center text-[11px] text-muted-foreground">
        No Expectation breakdown captured for this snapshot yet — recalculates on the next
        Discipline Rating confirmation or staffing change.
      </div>
    );
  }

  const { expectationLevel, penaltyStep, overall, members } = data;
  const totalPoints = members.reduce((sum, m) => sum + m.points, 0);
  const weightedSum = members.reduce((sum, m) => sum + m.memberScore * m.points, 0);

  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div
        className="mb-2 flex flex-wrap items-center justify-between gap-2"
        title="Expectation dimension — Discipline Rating (1–5) vs Expectation Level (1–5), points-weighted across Primaries."
      >
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Expectation Match breakdown
        </span>
        <span className="text-[10px] text-muted-foreground">
          Level {expectationLevel} · P={penaltyStep}
        </span>
      </div>

      {members.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-card/50 px-3 py-4 text-center text-[11px] text-muted-foreground">
          No Primaries staffed yet — the Expectation dimension has nothing to score.
        </div>
      ) : (
        <div className="space-y-1.5">
          {members.map((m) => {
            const badge = directionBadge(m.rating, expectationLevel, penaltyStep);
            return (
              <div
                key={m.resourceId}
                className="flex flex-wrap items-center justify-between gap-2 rounded border border-border bg-background px-2.5 py-1.5 text-[11px]"
              >
                <div className="flex min-w-0 items-center gap-1.5">
                  <span
                    className="h-2 w-2 shrink-0 rounded-sm"
                    style={{ background: ROLE_COLOR[m.role] ?? "var(--muted-foreground)" }}
                    aria-hidden
                  />
                  <span className="font-semibold">{m.role}</span>
                  <span className="truncate font-medium">{m.resourceName}</span>
                  <span className="text-muted-foreground">
                    {m.assignmentDisciplineCode ?? "no Discipline"}
                  </span>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-3 tabular-nums">
                  <span title="Discipline Rating (1–5) for the Assignment Discipline this Resource fills">
                    Rating <span className="font-semibold">{m.rating}</span>
                  </span>
                  <span title="Expectation Level (1–5)">
                    Level <span className="font-semibold">{expectationLevel}</span>
                  </span>
                  <span title="Deviation = |Discipline Rating − Expectation Level|">
                    Δ <span className="font-semibold">{m.deviation}</span>
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.className}`}
                    title={`Penalty = P × deviation = ${penaltyStep} × ${m.deviation} = ${badge.loss}`}
                  >
                    {badge.label}
                  </span>
                  <span title="Points weight (Primary role points)">Pts {m.points}</span>
                  <span
                    className="font-display font-bold"
                    title="memberScore = max(0, 100 − P × deviation)"
                  >
                    Score {m.memberScore.toFixed(1)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {members.length > 0 && (
        <div className="mt-2 border-t border-border pt-2 text-[10px] text-muted-foreground">
          ExpectationMatch = Σ(memberScore×points) ÷ Σ(points) = {weightedSum.toFixed(1)} ÷{" "}
          {totalPoints} ={" "}
          <span className="font-semibold text-foreground">{overall.toFixed(1)}</span>
        </div>
      )}
    </div>
  );
}
